import { Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { IEmployee } from 'src/app/employee/employee';
import { TimeEntryService } from './time-entry.service';

export type TimeEntryWriteAction = 'Create' | 'Update' | 'Delete';

/**
 * Client-side mirror of the server's write/sign reach for time data, shared by the time entry,
 * day and week grids so that nested grids do not each ask the server again.
 *
 * The cache lives as long as a grid is subscribed (refCount): leaving the page drops it, so a
 * changed role is picked up on the next visit.
 */
@Injectable({ providedIn: 'root' })
export class TimeEntryAccessService {

  constructor(private timeEntryService: TimeEntryService) {}

  /** Employees other than the current user whose days and weeks the sign right reaches. */
  readonly manageableEmployees$: Observable<IEmployee[]> =
    this.timeEntryService.getManageableEmployees().pipe(shareReplay({ bufferSize: 1, refCount: true }));

  readonly manageableEmployeeIds$: Observable<Set<string>> =
    this.manageableEmployees$.pipe(map(employees => new Set(employees.map(e => e.id))));

  /**
   * Employees other than the current user for whom a time entry may be created, updated or
   * deleted. Asked per action, because the rights behind them can be revoked one by one.
   */
  readonly writableEmployees: Record<TimeEntryWriteAction, Observable<IEmployee[]>> = {
    Create: this.writableEmployeesOf('Create'),
    Update: this.writableEmployeesOf('Update'),
    Delete: this.writableEmployeesOf('Delete')
  };

  private writableEmployeesOf(action: TimeEntryWriteAction): Observable<IEmployee[]> {
    return this.timeEntryService.getWritableEmployees(action)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }
}
